import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Select,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { GLASS_FORM } from '@/constants/design-tokens';
import { useCreatePO, useUpdatePO, usePurchaseOrder, purchaseOrdersApi, costToConfirmCount, GstTaxCell, PlaceOfSupply, gstTaxLines, isGstSplit } from '@/features/purchase-orders';
import type { CreatePOItemPayload, POUnit, PoDiscountType } from '@/features/purchase-orders';
import { computePoLine, toPoLineInput, taxRateOptions } from '@/features/purchase-orders/line-totals';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { exchangeRateService } from '@/services/exchange-rate.service';
import {
  PURCHASE_CURRENCY_OPTIONS,
  docCurrencySymbol,
  formatDocAmount,
  isValidExchangeRate,
  toTenantAmount,
} from '@/utils/purchase-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTenantCountry } from '@/hooks/use-tenant-country';
import { findCountry } from '@bbpos/types';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useRfq } from '@/features/rfqs';
import { suppliersApi } from '@/features/suppliers';
import type { SupplierListItem } from '@/features/suppliers';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { locationService } from '@/services/location.service';
import { productService } from '@/services/product.service';
import { ingredientService } from '@/services/ingredient.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { buildProductSearchParams } from './purchase-order-products';
import { poFormLabels } from './po-form-labels';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from './icons';

// ─── Constants ──────────────────────────────────────────────────────────────

const UNIT_OPTIONS: { value: POUnit; label: string }[] = [
  { value: 'PCS', label: 'Pieces' },
  { value: 'KG', label: 'Kilograms' },
  { value: 'G', label: 'Grams' },
  { value: 'L', label: 'Litres' },
  { value: 'ML', label: 'Millilitres' },
  { value: 'DOZEN', label: 'Dozen' },
  { value: 'BOX', label: 'Box' },
  { value: 'CARTON', label: 'Carton' },
];

const STEPS = [
  { id: 'supplier', label: 'Supplier Details' },
  { id: 'items', label: 'Add Items' },
  { id: 'review', label: 'Review & Submit' },
];

// ─── Item row interface ─────────────────────────────────────────────────────

interface ItemRow {
  key: string;
  product_id: string;
  product_name: string;
  /** Set when the line was picked from the catalog; shown under the name. */
  sku?: string;
  quantity: string;
  unit: POUnit;
  unit_cost: string;
  /** GST % as a string (slab picker value). */
  gst_rate: string;
  /** The picked product's purchase GST, restored if the supplier turns out domestic. */
  default_gst?: string;
  /** Set once the user picks a GST rate themselves; a supplier change then leaves it alone. */
  gst_touched?: boolean;
  discount_type: PoDiscountType;
  /** Percent (PERCENTAGE) or rupees (FLAT), raw input string. */
  discount_value: string;
}

function generateKey() {
  return Math.random().toString(36).slice(2, 10);
}

function blankItemRow(overrides: Partial<ItemRow> = {}): ItemRow {
  return {
    key: generateKey(),
    product_id: '',
    product_name: '',
    quantity: '',
    unit: 'PCS',
    unit_cost: '',
    gst_rate: '0',
    discount_type: 'PERCENTAGE',
    discount_value: '',
    ...overrides,
  };
}

/**
 * Opening lines for the wizard: one per repeated product_id/product_name query
 * pair (Low Stock "Create PO" / "Bulk Create PO"), or a single blank row. An
 * optional positional `quantity` (Products → Reorder "Create PO" sends the
 * suggested order qty; Low Stock sends threshold × 2 − stock) prefills the line
 * when it is a positive number, and an optional positional `sku` is shown under
 * the name. A product id passed twice opens one line.
 */
export function prefillItemRows(params: URLSearchParams): ItemRow[] {
  const names = params.getAll('product_name');
  const ids = params.getAll('product_id');
  const quantities = params.getAll('quantity');
  const skus = params.getAll('sku');
  const seen = new Set<string>();
  const rows = ids.flatMap((id, idx) => {
    if (!id || seen.has(id)) return [];
    seen.add(id);
    const qty = Number(quantities[idx]);
    return [
      blankItemRow({
        product_id: id,
        product_name: names[idx] ?? '',
        sku: skus[idx] || undefined,
        quantity: Number.isFinite(qty) && qty > 0 ? String(qty) : '',
      }),
    ];
  });
  return rows.length > 0 ? rows : [blankItemRow()];
}

/**
 * Start each still-blank line at its product's last received cost (I-1: the
 * most recent goods receipt, no fallback). `costs` is in minor units, keyed by
 * product id; a line the user has already priced is left as it is.
 */
export function applyLastReceivedCosts(
  rows: ItemRow[],
  costs: Record<string, number>,
  currency: string,
): ItemRow[] {
  const subunit = getSubunitMultiplier(currency);
  const decimals = getCurrencyDecimals(currency);
  return rows.map((row) => {
    const cost = costs[row.product_id];
    return row.unit_cost === '' && cost > 0 ? { ...row, unit_cost: (cost / subunit).toFixed(decimals) } : row;
  });
}

/**
 * Prefilled lines (Low Stock / Reorder) take each product's purchase tax rate
 * once it is loaded (QA PU2-002) — the URL carries none. A line that already
 * has a default (its product was picked meanwhile) is left alone.
 */
export function applyPrefillGst(
  rows: ItemRow[],
  rates: Record<string, number>,
  isForeignSupplier: boolean,
): ItemRow[] {
  return rows.map((row) => {
    const rate = rates[row.product_id];
    if (row.default_gst !== undefined || rate === undefined) return row;
    return defaultLineGst({ ...row, default_gst: String(rate) }, isForeignSupplier);
  });
}

/**
 * A line's GST for the supplier (client issue I-3, Q6): 0% for a supplier
 * registered in another country (customs and import VAT are outside the
 * system), else the product's purchase rate. A rate the user chose is kept.
 */
export function defaultLineGst<T extends Pick<ItemRow, 'gst_rate' | 'default_gst' | 'gst_touched'>>(
  row: T,
  isForeignSupplier: boolean,
): T {
  if (row.gst_touched) return row;
  const gst = isForeignSupplier ? '0' : row.default_gst ?? row.gst_rate;
  return gst === row.gst_rate ? row : { ...row, gst_rate: gst };
}

/**
 * Keys of the rows that repeat a product; a PO takes each product once.
 * Product rows are per location, so the same SKU picked from two locations is
 * the same product: rows compare by SKU when known, else by product id.
 */
export function repeatedProductRowKeys(rows: Pick<ItemRow, 'key' | 'product_id' | 'sku'>[]): string[] {
  const byProduct = new Map<string, string[]>();
  for (const row of rows) {
    if (!row.product_id) continue;
    const k = row.sku || row.product_id;
    byProduct.set(k, [...(byProduct.get(k) ?? []), row.key]);
  }
  return [...byProduct.values()].filter((keys) => keys.length > 1).flat();
}

/** "−OMR350.000 (10%)" / "−₹50.00" / "—" for a resolved line discount. */
function formatDiscountCell(
  lineDiscount: number,
  type: PoDiscountType,
  rawValue: string,
  currency: string,
): string {
  if (lineDiscount <= 0) return '—';
  const pct = type === 'PERCENTAGE' && parseFloat(rawValue) > 0 ? ` (${parseFloat(rawValue)}%)` : '';
  return `−${formatDocAmount(lineDiscount, currency)}${pct}`;
}

// ─── Content ────────────────────────────────────────────────────────────────

function PurchaseOrderCreateContent() {
  const dial = useTenantDial();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const tenantCurrency = useTenantCurrency();
  const tax = useTaxRegime();
  // The PO's own currency (docs/multi-currency-purchasing-plan.md D1): every
  // amount typed and shown on this wizard is in it. It starts as the tenant's
  // and follows a picked supplier's; a foreign one needs an exchange rate.
  // Only another currency is stored: null follows the tenant's, which may
  // still be loading when the wizard mounts.
  const [currencyChoice, setCurrencyChoice] = useState<string | null>(null);
  const currency = currencyChoice ?? tenantCurrency;
  const isForeignCurrency = currency !== tenantCurrency;
  const [exchangeRate, setExchangeRate] = useState('');
  const [rateTouched, setRateTouched] = useState(false);
  const symbol = docCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const createMutation = useCreatePO();
  const updateMutation = useUpdatePO();

  // Editing an existing DRAFT: /inventory/purchase-orders/:id/edit reuses this
  // wizard rather than duplicating supplier search, line editing and totals.
  // The API refuses a PUT on anything past DRAFT, so the detail page only
  // offers Edit while the PO is a draft.
  const { id: editId } = useParams<{ id: string }>();
  const isEditing = Boolean(editId);
  const { data: editingPo, isLoading: editLoading } = usePurchaseOrder(editId);
  const [editSeeded, setEditSeeded] = useState(false);
  const labels = poFormLabels(isEditing, editingPo?.po_number);

  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Raised from an RFQ (?rfq_id=&rfq_supplier_id=): the supplier is fixed to the
  // chosen invited supplier and the lines/costs are seeded from their quote.
  // Quantities stay editable and lines can be added/removed.
  const rfqId = searchParams.get('rfq_id') ?? undefined;
  const rfqSupplierId = searchParams.get('rfq_supplier_id') ?? undefined;
  const fromRfq = Boolean(rfqId && rfqSupplierId);
  const { data: sourceRfq, isLoading: rfqLoading } = useRfq(fromRfq ? rfqId : undefined);
  const rfqSupplier = sourceRfq?.suppliers.find((s) => s.id === rfqSupplierId) ?? null;
  const [rfqSeeded, setRfqSeeded] = useState(false);

  // Step 1: Supplier
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [supplierEmail, setSupplierEmail] = useState('');
  // Country of the directory supplier picked (null = the tenant's own, or a
  // typed name not in the directory). A foreign one starts lines at 0% (Q6).
  const [supplierCountry, setSupplierCountry] = useState<string | null>(null);
  const tenantCountry = useTenantCountry();
  const isForeignSupplier = !!supplierCountry && supplierCountry !== tenantCountry.iso;
  const [locationId, setLocationId] = useState(selectedLocationId ?? '');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');

  // Supplier directory: searchable + editable combobox.
  // Typing 3+ letters fetches matching active suppliers from the directory.
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const supplierBoxRef = useRef<HTMLDivElement>(null);
  const supplierQuery = supplierName.trim();

  const { data: supplierMatchesData, isFetching: supplierMatchesLoading } = useQuery({
    queryKey: ['po-supplier-search', supplierQuery],
    queryFn: () => suppliersApi.list({ search: supplierQuery, is_active: true, limit: 10 }),
    enabled: supplierQuery.length >= 3 && showSupplierDropdown,
    staleTime: 15_000,
  });
  const supplierMatches = supplierMatchesData?.data ?? [];

  function handleSupplierNameChange(value: string) {
    setSupplierName(value);
    // Editing the name detaches any previously linked directory supplier.
    if (supplierId) setSupplierId('');
    setSupplierCountry(null);
    setShowSupplierDropdown(true);
  }

  function selectDirectorySupplier(supplier: SupplierListItem) {
    setSupplierId(supplier.id);
    setSupplierName(supplier.name);
    setSupplierPhone(supplier.phone || '');
    setSupplierEmail(supplier.email || '');
    setSupplierCountry(supplier.country_code ?? null);
    changePoCurrency(supplier.currency_code ?? tenantCurrency);
    setShowSupplierDropdown(false);
  }

  // Close the supplier dropdown on outside click
  useEffect(() => {
    if (!showSupplierDropdown) return;
    function onClickOutside(e: MouseEvent) {
      if (supplierBoxRef.current && !supplierBoxRef.current.contains(e.target as Node)) {
        setShowSupplierDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showSupplierDropdown]);

  // Step 2: Items. When arriving from a Low Stock "Create PO" action (issue #228)
  // or "Bulk Create PO" (QA 6.8.15), the products are passed as repeated
  // product_id/product_name query params and the wizard opens with one line per
  // product; otherwise start with one blank row.
  const [items, setItems] = useState<ItemRow[]>(() => prefillItemRows(searchParams));
  const [productSearch, setProductSearch] = useState('');

  // Seed once from the RFQ. Unit cost / GST come from this supplier's quote for
  // the line (blank when they didn't quote it — the cashier fills it in).
  useEffect(() => {
    if (!fromRfq || rfqSeeded || !sourceRfq) return;
    if (!rfqSupplier) {
      toast({ title: 'That supplier is not on the RFQ', variant: 'error' });
      navigate(`/inventory/rfqs/${sourceRfq.id}`);
      return;
    }
    if (sourceRfq.status !== 'SENT') {
      toast({ title: `Purchase orders can only be raised from a SENT RFQ (this one is ${sourceRfq.status.toLowerCase()})`, variant: 'error' });
      navigate(`/inventory/rfqs/${sourceRfq.id}`);
      return;
    }
    setSupplierId(rfqSupplier.supplier_id);
    setSupplierName(rfqSupplier.supplier_name);
    setSupplierPhone(rfqSupplier.supplier_phone ?? '');
    setSupplierEmail(rfqSupplier.supplier_email ?? '');
    setLocationId(sourceRfq.location_id);
    setExpectedDate(sourceRfq.expected_delivery_date ? sourceRfq.expected_delivery_date.slice(0, 10) : '');
    setNotes(sourceRfq.notes ?? '');
    setItems(sourceRfq.items.map((i) => {
      const q = rfqSupplier.quotes.find((x) => x.rfq_item_id === i.id);
      return blankItemRow({
        product_id: (i.product_id ?? i.ingredient_id) as string,
        product_name: i.item_name,
        quantity: String(i.quantity),
        unit: i.unit,
        unit_cost: q ? (q.unit_cost / subunit).toFixed(getCurrencyDecimals(currency)) : '',
        gst_rate: String(q?.gst_rate ?? 0),
        // The supplier quoted this rate: a supplier change must not reset it.
        gst_touched: true,
        // A FLAT quote discount is per unit; PO FLAT is per line, so × qty.
        // PERCENTAGE is basis points (always /100); FLAT is minor units (/subunit).
        discount_type: q?.discount_type ?? 'PERCENTAGE',
        discount_value: q && q.discount_value
          ? (q.discount_type === 'FLAT'
              ? (Math.floor(q.discount_value * i.quantity) / subunit).toFixed(getCurrencyDecimals(currency))
              // not-money: basis points → percent
              : (q.discount_value / 100).toFixed(2))
          : '',
      });
    }));
    setRfqSeeded(true);
  }, [fromRfq, rfqSeeded, sourceRfq, rfqSupplier, navigate, toast]);

  // Locations
  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];

  // Restaurant tenants purchase INGREDIENTS (raw materials), retail tenants
  // purchase PRODUCTS. The picker + submitted line type switch on this.
  const businessType = useAuthStore((s) => s.profile?.tenant?.business_type);
  const isRestaurant = businessType === 'RESTAURANT';

  // Products for item search (retail). A PO brings new stock into a location,
  // so the picker lists every active product in the tenant catalog. The list is
  // capped, so the line picker's typed text searches here too (QA PU3-001); the
  // last list stays up while the next one loads.
  const { data: productsData } = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => productService.list(buildProductSearchParams(productSearch)),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    enabled: !isRestaurant,
  });
  // Ingredients for item search (restaurant).
  const { data: ingredientsData } = useQuery({
    queryKey: ['ingredients-search'],
    queryFn: () => ingredientService.listIngredients({ is_active: 'true', limit: 200 }),
    staleTime: 15_000,
    enabled: isRestaurant,
  });

  // Unified catalog: {id, name, label, cost, gst_rate} regardless of vertical. The
  // item row still keys off `product_id` — for restaurant it holds the ingredient id.
  // gst_rate is the product's *purchase* GST (product_purchase_gst side table,
  // returned by the products list when present) falling back to its sales rate;
  // ingredients carry no rate.
  // A different supplier re-defaults the lines whose GST the user hasn't set.
  useEffect(() => {
    setItems((prev) => {
      const next = prev.map((r) => defaultLineGst(r, isForeignSupplier));
      return next.every((r, i) => r === prev[i]) ? prev : next;
    });
  }, [isForeignSupplier]);

  // Lines carried in from Low Stock or Reorder start at each product's last
  // received cost. Applied once when it arrives; a cost typed meanwhile stays.
  const prefillIds = useMemo(
    () => [...new Set(searchParams.getAll('product_id').filter(Boolean))],
    [searchParams],
  );
  const { data: lastCosts } = useQuery({
    queryKey: ['po-last-received-costs', prefillIds, currency],
    queryFn: () => purchaseOrdersApi.lastReceivedCosts(prefillIds, isForeignCurrency ? currency : undefined),
    enabled: !isEditing && !isRestaurant && prefillIds.length > 0,
    staleTime: Infinity,
  });
  const lastCostsApplied = useRef(false);
  useEffect(() => {
    if (!lastCosts || lastCostsApplied.current) return;
    lastCostsApplied.current = true;
    setItems((prev) => applyLastReceivedCosts(prev, lastCosts, currency));
  }, [lastCosts, currency]);

  // ...and at each product's purchase tax rate (QA PU2-002), not 0%.
  const { data: prefillGst } = useQuery({
    queryKey: ['po-prefill-purchase-gst', prefillIds],
    queryFn: async () => {
      const products = await Promise.all(prefillIds.map((pid) => productService.getById(pid).catch(() => null)));
      return Object.fromEntries(
        products.flatMap((p) => (p ? [[p.id, (p as { purchase_gst_rate?: number | null }).purchase_gst_rate ?? p.gst_rate ?? 0]] : [])),
      ) as Record<string, number>;
    },
    enabled: !isEditing && !isRestaurant && prefillIds.length > 0,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (!prefillGst) return;
    setItems((prev) => {
      const next = applyPrefillGst(prev, prefillGst, isForeignSupplier);
      return next.every((r, i) => r === prev[i]) ? prev : next;
    });
  }, [prefillGst, isForeignSupplier]);

  /**
   * Put the PO in another currency. Costs and flat discounts already typed
   * were in the old one, so they are cleared (percentages stay) and the last
   * received costs are fetched again in the new one; the rate starts over.
   */
  function changePoCurrency(next: string) {
    if (next === currency) return;
    setCurrencyChoice(next === tenantCurrency ? null : next);
    setExchangeRate('');
    setRateTouched(false);
    setItems((prev) => prev.map((r) => ({
      ...r,
      unit_cost: '',
      discount_value: r.discount_type === 'FLAT' ? '' : r.discount_value,
    })));
    lastCostsApplied.current = false;
    setErrors((prev) => {
      const kept = Object.entries(prev).filter(([k]) => k !== 'exchangeRate' && !k.endsWith('_cost'));
      return Object.fromEntries(kept);
    });
  }

  // A foreign currency starts at the rate on the Exchange Rates page (D4); a
  // rate the user typed is kept.
  const { data: suggestedRate } = useQuery({
    queryKey: ['po-exchange-rate', currency, tenantCurrency],
    queryFn: () => exchangeRateService.lookup(currency, tenantCurrency),
    enabled: isForeignCurrency,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!isForeignCurrency || rateTouched || !suggestedRate) return;
    setExchangeRate(suggestedRate.rate.replace(/\.?0+$/, ''));
  }, [isForeignCurrency, rateTouched, suggestedRate]);

  const catalog = useMemo(() => {
    if (isRestaurant) {
      return (ingredientsData?.data ?? []).map((g) => ({ id: g.id, name: g.name, label: g.name, sku: undefined as string | undefined, cost: g.cost, gst_rate: 0 }));
    }
    // Each store has its own row of a product; name the store so the rows can
    // be told apart (QA PU3-001). Receiving posts to the PO's store either way.
    const storeName = new Map(locations.map((l) => [l.id, l.name]));
    return (productsData?.data ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      label: locations.length > 1 && storeName.has(p.location_id)
        ? `${p.name} (${p.sku}) · ${storeName.get(p.location_id)}`
        : `${p.name} (${p.sku})`,
      sku: p.sku as string | undefined,
      cost: p.cost,
      gst_rate: (p as { purchase_gst_rate?: number | null }).purchase_gst_rate ?? p.gst_rate ?? 0,
    }));
  }, [isRestaurant, ingredientsData, productsData, locations]);

  // Options excluding already-selected rows. Retail filters server-side via
  // productSearch; ingredients are all loaded, so filter them client-side here.
  const selectedProductIds = useMemo(() => new Set(items.map((i) => i.product_id).filter(Boolean)), [items]);
  const productOptions = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return catalog
      .filter((c) => !selectedProductIds.has(c.id))
      .filter((c) => !isRestaurant || !q || c.name.toLowerCase().includes(q))
      .map((c) => ({ value: c.id, label: c.label }));
  }, [catalog, selectedProductIds, isRestaurant, productSearch]);

  // Escape leaves the wizard; ask first once anything has been filled in.
  const formDirty =
    supplierName.trim() !== '' ||
    expectedDate !== '' ||
    notes.trim() !== '' ||
    items.some((i) => i.product_id !== '');
  const { confirmLeave, dialog: leaveDialog } = useUnsavedChangesGuard(formDirty);

  // Keyboard: Ctrl+Enter to submit, Esc to go back
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // A dropdown that was open handled this Escape (closed itself).
      if (e.key === 'Escape' && e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        confirmLeave(() => navigate('/inventory/purchase-orders'));
      }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && currentStep === 2) {
        e.preventDefault();
        handleSubmit();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [currentStep, navigate, confirmLeave]);

  // Validate step 1
  function validateStep1(): boolean {
    const errs: Record<string, string> = {};
    if (!supplierName.trim()) errs.supplierName = 'Supplier name is required';
    if (!locationId) errs.locationId = 'Location is required';
    if (supplierPhone && !/^\+[1-9]\d{6,14}$/.test(supplierPhone)) {
      errs.supplierPhone = 'Phone must be in E.164 format (e.g. +919876543210)';
    }
    if (supplierEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierEmail)) {
      errs.supplierEmail = 'Invalid email address';
    }
    if (isForeignCurrency && !isValidExchangeRate(exchangeRate)) {
      errs.exchangeRate = `Enter how much ${tenantCurrency} 1 ${currency} is (up to 8 decimals)`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // Validate step 2
  function validateStep2(): boolean {
    const errs: Record<string, string> = {};
    const validItems = items.filter((i) => i.product_id);
    if (validItems.length === 0) {
      errs.items = 'Add at least one item';
      setErrors(errs);
      return false;
    }
    for (const k of repeatedProductRowKeys(validItems)) {
      errs[`item_${k}_product`] = 'This product is on more than one line';
    }
    for (let idx = 0; idx < validItems.length; idx++) {
      const item = validItems[idx];
      const qty = parseFloat(item.quantity);
      const cost = parseFloat(item.unit_cost);
      // Keyed by row key, not index: `idx` walks validItems (rows with a
      // product) while the table renders every row, so an index key paints the
      // error on the wrong line whenever a blank row sits above a filled one.
      if (!qty || qty <= 0) errs[`item_${item.key}_qty`] = 'Quantity must be > 0';
      // A blank or 0 cost is allowed: the line is saved as "to be confirmed" and
      // the owner prices it before approving (I-1 Phase B).
      if (cost < 0) errs[`item_${item.key}_cost`] = 'Unit cost cannot be negative';
      // Sent in minor units: a typed cost that rounds to 0 (0.0004 OMR) is a mistake, not "to confirm".
      else if (cost > 0 && Math.round(cost * subunit) < 1) {
        errs[`item_${item.key}_cost`] = `Unit cost must be at least ${(1 / subunit).toFixed(getCurrencyDecimals(currency))}`;
      }
      // Discount errors are keyed by row key (not index) so they land on the
      // right row even when blank rows sit between valid ones.
      const discount = parseFloat(item.discount_value) || 0;
      if (discount < 0) errs[`item_${item.key}_discount`] = 'Discount cannot be negative';
      else if (item.discount_type === 'PERCENTAGE' && discount > 100) {
        errs[`item_${item.key}_discount`] = 'Discount cannot exceed 100%';
        // Both sides scale by the same factor, so this comparison is
        // currency-independent — the literal is a shared scale, not a conversion.
        // not-money: both sides scale by the same factor, so this is a shared scale
      } else if (item.discount_type === 'FLAT' && qty > 0 && discount * 100 > Math.floor(qty * (cost || 0) * 100)) {
        errs[`item_${item.key}_discount`] = 'Discount cannot exceed the line amount';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (currentStep === 0 && !validateStep1()) return;
    if (currentStep === 1 && !validateStep2()) return;
    setCurrentStep((s) => Math.min(s + 1, 2));
  }

  function handleBack() {
    setErrors({});
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  function addItemRow() {
    setItems((prev) => [...prev, blankItemRow()]);
  }

  function removeItemRow(key: string) {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((i) => i.key !== key);
    });
  }

  function updateItem(key: string, field: keyof ItemRow, value: string) {
    // Editing a field answers its error, so a corrected value no longer shows
    // its old error until the next Next. A product change can end a repeat on
    // another row, so it clears every row's product error.
    const errorKey =
      field === 'quantity' ? `item_${key}_qty`
      : field === 'unit_cost' ? `item_${key}_cost`
      : field === 'discount_value' || field === 'discount_type' ? `item_${key}_discount`
      : null;
    if (errorKey || field === 'product_id') {
      setErrors((prev) => {
        const kept = Object.entries(prev).filter(
          ([k]) => k !== errorKey && !(field === 'product_id' && k.endsWith('_product')),
        );
        return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
      });
    }
    setItems((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        const updated = { ...i, [field]: value };
        // Auto-fill name, cost and purchase GST when a catalog item is picked
        if (field === 'product_id') {
          const picked = catalog.find((c) => c.id === value);
          updated.sku = picked?.sku;
          if (picked) {
            updated.product_name = picked.name;
            if (!i.unit_cost && picked.cost && !isForeignCurrency) {
              updated.unit_cost = (picked.cost / subunit).toFixed(getCurrencyDecimals(currency));
            }
            // A new product gets a fresh default: its purchase GST, or 0% for a
            // supplier registered in another country (Q6).
            updated.default_gst = String(picked.gst_rate ?? 0);
            updated.gst_touched = false;
            Object.assign(updated, defaultLineGst(updated, isForeignSupplier));
          }
        }
        if (field === 'gst_rate') updated.gst_touched = true;
        return updated;
      }),
    );
  }

  // Seed the wizard from the PO being edited. Runs once: after that the user's
  // edits own the state.
  useEffect(() => {
    if (!isEditing || editSeeded || !editingPo) return;
    if (editingPo.status !== 'DRAFT') {
      toast({ title: `A ${editingPo.status.toLowerCase()} purchase order cannot be edited`, variant: 'error' });
      navigate(`/inventory/purchase-orders/${editingPo.id}`);
      return;
    }
    setSupplierId(editingPo.supplier_id ?? '');
    setSupplierName(editingPo.supplier_name);
    setSupplierPhone(editingPo.supplier_phone ?? '');
    setSupplierEmail(editingPo.supplier_email ?? '');
    // The draft's own currency and rate; its amounts are in that currency.
    const seedCurrency = editingPo.currency_code ?? tenantCurrency;
    const seedSubunit = getSubunitMultiplier(seedCurrency);
    const seedDecimals = getCurrencyDecimals(seedCurrency);
    setCurrencyChoice(editingPo.currency_code ?? null);
    setExchangeRate(editingPo.exchange_rate ? editingPo.exchange_rate.replace(/\.?0+$/, '') : '');
    setRateTouched(true);
    setLocationId(editingPo.location_id);
    setExpectedDate(editingPo.expected_delivery_date ? editingPo.expected_delivery_date.slice(0, 10) : '');
    setNotes(editingPo.notes ?? '');
    setItems(
      editingPo.items.map((i) =>
        blankItemRow({
          product_id: i.product_id as string,
          product_name: i.product_name,
          quantity: String(Number(i.quantity)),
          unit: i.unit as POUnit,
          unit_cost: (Number(i.unit_cost) / seedSubunit).toFixed(seedDecimals),
          gst_rate: String(i.gst_rate ?? 0),
          // A saved draft's rates are kept as they are.
          gst_touched: true,
          discount_type: (i.discount_type as PoDiscountType) ?? 'PERCENTAGE',
          // PERCENTAGE is basis points (always /100); FLAT is minor units (/subunit).
          discount_value: i.discount_value
            ? i.discount_type === 'FLAT'
              ? (Number(i.discount_value) / seedSubunit).toFixed(seedDecimals)
              // not-money: basis points → percent
              : String(Number(i.discount_value) / 100)
            : '',
        }),
      ),
    );
    setEditSeeded(true);
  }, [isEditing, editSeeded, editingPo, navigate, toast, tenantCurrency]);

  // A line without a cost still counts: it is saved at 0, "to be confirmed".
  const validItems = useMemo(
    () => items.filter((i) => i.product_id && parseFloat(i.quantity) > 0),
    [items],
  );
  const costToConfirm = useMemo(
    () => costToConfirmCount(validItems.map((i) => toPoLineInput(i, currency))),
    [validItems, currency],
  );

  // Per-line breakdown (paise ints) via the shared helper — the same math the
  // API stores, so the review figures match the created PO exactly.
  const lineTotals = useMemo(
    () => new Map(validItems.map((i) => [i.key, computePoLine(toPoLineInput(i, currency), tenantCurrency)])),
    [validItems, currency, tenantCurrency],
  );
  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, tax = 0, grand = 0;
    for (const t of lineTotals.values()) {
      subtotal += t.line_subtotal;
      discount += t.line_discount;
      tax += t.line_tax;
      grand += t.line_total;
    }
    return { subtotal, discount, tax, grand };
  }, [lineTotals]);
  const grandTotal = totals.grand;
  // CGST + SGST or IGST from the store and the picked supplier (QA PU1-004).
  const { data: supply } = useQuery({
    queryKey: ['po-supply-preview', locationId, supplierId],
    queryFn: () => purchaseOrdersApi.supplyPreview(locationId, supplierId),
    enabled: !!locationId,
    staleTime: 60_000,
  });
  const taxLines = useMemo(
    () => (isGstSplit(supply)
      ? gstTaxLines(supply, validItems.map((i) => ({ gst_rate: toPoLineInput(i, currency).gst_rate ?? 0, line_tax: lineTotals.get(i.key)?.line_tax ?? 0 })))
      : []),
    [supply, validItems, lineTotals, currency],
  );
  // ≈ the total in the tenant currency at the typed rate (null while invalid).
  const tenantGrandTotal = isForeignCurrency
    ? toTenantAmount(grandTotal, currency, tenantCurrency, exchangeRate)
    : null;

  const handleSubmit = useCallback(() => {
    const payload = {
      location_id: locationId,
      supplier_id: supplierId || undefined,
      ...(fromRfq ? { rfq_id: rfqId, rfq_supplier_id: rfqSupplierId } : {}),
      supplier_name: supplierName.trim(),
      supplier_phone: supplierPhone || undefined,
      supplier_email: supplierEmail || undefined,
      expected_delivery_date: expectedDate || undefined,
      notes: notes.trim() || undefined,
      // Omitted = the tenant currency (also what turns an edited draft back).
      ...(isForeignCurrency ? { currency_code: currency, exchange_rate: exchangeRate.trim() } : {}),
      items: validItems.map((i): CreatePOItemPayload => {
        const line = toPoLineInput(i, currency);
        return {
          // Restaurant lines carry ingredient_id; retail lines carry product_id.
          ...(isRestaurant ? { ingredient_id: i.product_id } : { product_id: i.product_id }),
          quantity: line.quantity,
          unit: i.unit,
          unit_cost: line.unit_cost,
          ...(line.gst_rate ? { gst_rate: line.gst_rate } : {}),
          ...(line.discount_value
            ? { discount_type: line.discount_type, discount_value: line.discount_value }
            : {}),
        };
      }),
    };

    const run = isEditing
      ? (body: typeof payload, opts: Parameters<typeof createMutation.mutate>[1]) =>
          updateMutation.mutate({ id: editId as string, payload: body }, opts as never)
      : (body: typeof payload, opts: Parameters<typeof createMutation.mutate>[1]) =>
          createMutation.mutate(body, opts);

    run(payload, {
      onSuccess: (result) => {
        // The dropdown offers to "add <name> as a new supplier" when nothing
        // matches. The server now honours that, so say so — worded to hold
        // whether it created the record or matched an existing one on phone,
        // email or name.
        const supplierNote = supplierId ? '' : ` · ${supplierName.trim()} is in your supplier directory`;
        toast({
          title: isEditing
            ? `Purchase order ${result.po_number} updated`
            : `Purchase order ${result.po_number} created${fromRfq && sourceRfq ? ` from ${sourceRfq.rfq_number}` : ''}${supplierNote}`,
          variant: 'success',
        });
        navigate(`/inventory/purchase-orders/${result.id}`);
      },
      onError: (err: Error) => {
        toast({ title: err.message, variant: 'error' });
      },
    });
  }, [locationId, supplierId, supplierName, supplierPhone, supplierEmail, expectedDate, notes, validItems, isRestaurant, createMutation, updateMutation, isEditing, editId, navigate, toast, fromRfq, rfqId, rfqSupplierId, currency, isForeignCurrency, exchangeRate]);

  const stepIndex = currentStep;

  return (
    <>
      {/* Gradient header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative">
          <h1 className="text-2xl font-bold tracking-tight text-white">{labels.title}</h1>
          <p className="text-teal-100 mt-1">
            {fromRfq && sourceRfq
              ? `From ${sourceRfq.rfq_number} · supplier fixed to ${rfqSupplier?.supplier_name ?? '…'} — adjust quantities and lines as needed`
              : labels.subtitle}
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6 animate-slide-up">
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center gap-2">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all duration-200 ${
                idx <= stepIndex
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
              }`}
            >
              {idx + 1}
            </div>
            <span
              className={`text-sm font-medium ${
                idx <= stepIndex
                  ? 'text-neutral-900 dark:text-white'
                  : 'text-neutral-400 dark:text-neutral-500'
              }`}
            >
              {step.label}
            </span>
            {idx < STEPS.length - 1 && (
              <div
                className={`w-12 h-0.5 ${
                  idx < stepIndex ? 'bg-teal-500' : 'bg-neutral-200 dark:bg-neutral-700'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className={`${GLASS_FORM} p-6 animate-slide-up`}>
        {/* Step 1: Supplier Details */}
        {stepIndex === 0 && (
          <div className="space-y-5 max-w-2xl">
            {fromRfq ? (
            <div className="rounded-xl border border-brand-200 dark:border-brand-800 bg-brand-50/60 dark:bg-brand-900/20 p-4">
              <p className="text-xs font-medium text-brand-700 dark:text-brand-200 uppercase tracking-wide mb-1">Supplier (from {sourceRfq?.rfq_number ?? 'RFQ'})</p>
              <p className="text-base font-semibold text-neutral-900 dark:text-white">{supplierName || (rfqLoading || editLoading ? 'Loading…' : '—')}</p>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{[supplierPhone, supplierEmail].filter(Boolean).join(' · ') || 'No contact details on file'}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">The supplier on a PO raised from an RFQ cannot be changed. Quantities and line items can.</p>
            </div>
            ) : (
            <div ref={supplierBoxRef} className="relative">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Supplier Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={supplierName}
                onChange={(e) => handleSupplierNameChange(e.target.value)}
                onFocus={() => setShowSupplierDropdown(true)}
                placeholder="Type a supplier name to search the directory, or enter manually"
                autoComplete="off"
                role="combobox"
                aria-label="Supplier Name"
                aria-expanded={showSupplierDropdown}
                aria-autocomplete="list"
                className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
              />
              {showSupplierDropdown && supplierQuery.length >= 3 && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg max-h-60 overflow-y-auto animate-fade-in">
                  {supplierMatchesLoading ? (
                    <div className="px-3 py-2 text-sm text-neutral-400 dark:text-neutral-500">
                      Searching…
                    </div>
                  ) : supplierMatches.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-neutral-400 dark:text-neutral-500">
                      No matching supplier — press Next to add “{supplierQuery}” as a new supplier
                    </div>
                  ) : (
                    supplierMatches.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectDirectorySupplier(s);
                        }}
                        className="flex w-full flex-col items-start px-3 py-2 text-left text-sm text-neutral-900 dark:text-neutral-100 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                      >
                        <span className="font-medium">{s.name}</span>
                        {(s.phone || s.email) && (
                          <span className="text-xs text-neutral-400 dark:text-neutral-500">
                            {[s.phone, s.email].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
              {errors.supplierName && (
                <p className="text-xs text-danger-600 mt-1">{errors.supplierName}</p>
              )}
              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                Type 3+ letters to search your supplier directory, or just enter a new name manually
              </p>
            </div>
            )}

            {!fromRfq && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Supplier Phone
                </label>
                <Input
                  variant="phone"
                  defaultDial={dial}
                  value={supplierPhone}
                  onChange={(v) => setSupplierPhone(v)}
                  placeholder="98765 43210"
                />
                {errors.supplierPhone && (
                  <p className="text-xs text-danger-600 mt-1">{errors.supplierPhone}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Supplier Email
                </label>
                <Input
                  type="email"
                  value={supplierEmail}
                  onChange={(v) => setSupplierEmail(v)}
                  placeholder="supplier@example.com"
                />
                {errors.supplierEmail && (
                  <p className="text-xs text-danger-600 mt-1">{errors.supplierEmail}</p>
                )}
              </div>
            </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Receiving Location <span className="text-danger-500">*</span>
                </label>
                <Select
                  options={locations.map((l) => ({ value: l.id, label: l.name }))}
                  value={locationId}
                  onChange={(v) => setLocationId(typeof v === 'string' ? v : '')}
                  placeholder="Select location"
                />
                {errors.locationId && (
                  <p className="text-xs text-danger-600 mt-1">{errors.locationId}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expected Delivery Date
                </label>
                <Input
                  type="date"
                  value={expectedDate}
                  onChange={(v) => setExpectedDate(v)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  variant="searchable"
                  label="Currency"
                  options={PURCHASE_CURRENCY_OPTIONS}
                  value={currency}
                  onChange={(v) => { if (typeof v === 'string' && v) changePoCurrency(v); }}
                  disabled={fromRfq}
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  {fromRfq
                    ? `A purchase order from an RFQ is in ${tenantCurrency}, like its quotes.`
                    : 'Prices on this order are in this currency.'}
                </p>
              </div>
              {isForeignCurrency && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Exchange rate: 1 {currency} in {tenantCurrency} <span className="text-danger-600">*</span>
                  </label>
                  <Input
                    value={exchangeRate}
                    inputMode="decimal"
                    placeholder="e.g. 0.0046"
                    onChange={(v) => {
                      setExchangeRate(v.trim());
                      setRateTouched(true);
                      if (errors.exchangeRate) setErrors((e) => ({ ...e, exchangeRate: '' }));
                    }}
                  />
                  {isValidExchangeRate(exchangeRate) ? (
                    <p className="text-xs text-teal-700 dark:text-teal-300 mt-1 tabular-nums">
                      1 {currency} = {exchangeRate} {tenantCurrency} · 1 {tenantCurrency} ={' '}
                      {(1 / Number(exchangeRate)).toLocaleString('en-US', { maximumFractionDigits: 4 })} {currency}
                      {!rateTouched && suggestedRate ? ' · from Exchange Rates' : ''}
                    </p>
                  ) : (
                    !errors.exchangeRate && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Stock cost and what you owe are worked out in {tenantCurrency} at this rate.
                      </p>
                    )
                  )}
                  {errors.exchangeRate && (
                    <p className="text-xs text-danger-600 mt-1">{errors.exchangeRate}</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes for this purchase order..."
                rows={3}
                className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                maxLength={1000}
              />
            </div>
          </div>
        )}

        {/* Step 2: Add Items */}
        {stepIndex === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                Order Items
              </h3>
              <div className="w-56">
                <Input
                  placeholder={isRestaurant ? 'Search ingredients...' : 'Search products...'}
                  value={productSearch}
                  onChange={(v) => setProductSearch(v)}
                />
              </div>
            </div>

            {errors.items && (
              <p className="text-xs text-danger-600">{errors.items}</p>
            )}

            {isForeignSupplier && (
              <p className="rounded-lg bg-brand-50 dark:bg-brand-900/20 px-3 py-2 text-xs text-brand-800 dark:text-brand-200">
                Supplier registered in {findCountry(supplierCountry)?.name ?? supplierCountry}: lines start at 0% tax.
                Customs and import VAT aren&apos;t added here.
              </p>
            )}

            {/* overflow-visible (not overflow-x-auto): overflow-x:auto forces
                overflow-y to auto too, which clips the open product/unit
                dropdowns (especially when they flip upward). */}
            <div className="overflow-visible">
              {/* table-fixed: the Select/Input controls have intrinsic min-widths
                  that, under auto layout, push the row wider than the card. With
                  fixed layout the column widths win and the w-full controls
                  shrink to fit; Product takes whatever is left. */}
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">{isRestaurant ? 'Ingredient' : 'Product'}</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-24">Quantity</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Unit</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Unit Cost</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-44">Discount</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-24">{tax.taxName} %</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-36 whitespace-nowrap">Line Total</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const line = computePoLine(toPoLineInput(item, currency), tenantCurrency);
                    const lineTotal = line.line_total;
                    const discountError = errors[`item_${item.key}_discount`];
                    return (
                      <tr key={item.key} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
                        <td className="py-2 pr-3">
                          <Select
                            variant="searchable"
                            options={[
                              ...(item.product_id
                                ? [{ value: item.product_id, label: item.product_name || item.product_id }]
                                : []),
                              ...productOptions,
                            ]}
                            value={item.product_id}
                            onChange={(v) => updateItem(item.key, 'product_id', typeof v === 'string' ? v : '')}
                            onSearch={setProductSearch}
                            placeholder={isRestaurant ? 'Select ingredient' : 'Select product'}
                          />
                          {item.sku && (
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">SKU: {item.sku}</p>
                          )}
                          {errors[`item_${item.key}_product`] && (
                            <p className="text-xs text-danger-600 mt-0.5">{errors[`item_${item.key}_product`]}</p>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(v) => updateItem(item.key, 'quantity', v)}
                            placeholder="0"
                          />
                          {errors[`item_${item.key}_qty`] && (
                            <p className="text-xs text-danger-600 mt-0.5">{errors[`item_${item.key}_qty`]}</p>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Select
                            options={UNIT_OPTIONS}
                            value={item.unit}
                            onChange={(v) => updateItem(item.key, 'unit', typeof v === 'string' ? v : 'PCS')}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            type="number"
                            value={item.unit_cost}
                            onChange={(v) => updateItem(item.key, 'unit_cost', v)}
                            step={1 / subunit}
                            placeholder={(0).toFixed(getCurrencyDecimals(currency))}
                          />
                          {errors[`item_${item.key}_cost`] ? (
                            <p className="text-xs text-danger-600 mt-0.5">{errors[`item_${item.key}_cost`]}</p>
                          ) : item.product_id && !(parseFloat(item.unit_cost) > 0) ? (
                            <p className="text-xs text-warning-700 dark:text-warning-300 mt-0.5">Cost to be confirmed before approval</p>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">
                          {/* %/\u20b9 toggle + amount. Switching the mode keeps the typed number. */}
                          <div className="flex items-stretch gap-1">
                            <div
                              role="radiogroup"
                              aria-label="Discount type"
                              className="flex rounded-lg border border-neutral-300 dark:border-neutral-600 overflow-hidden shrink-0"
                            >
                              {(['PERCENTAGE', 'FLAT'] as const).map((t) => (
                                <button
                                  key={t}
                                  type="button"
                                  role="radio"
                                  aria-checked={item.discount_type === t}
                                  onClick={() => updateItem(item.key, 'discount_type', t)}
                                  className={`px-2 text-xs font-semibold transition-colors duration-200 ${
                                    item.discount_type === t
                                      ? 'bg-teal-600 text-white'
                                      : 'bg-white dark:bg-neutral-800 text-neutral-500 hover:bg-teal-50 dark:hover:bg-neutral-700'
                                  }`}
                                  title={t === 'PERCENTAGE' ? 'Percent off' : 'Flat amount off'}
                                >
                                  {t === 'PERCENTAGE' ? '%' : symbol}
                                </button>
                              ))}
                            </div>
                            {/* flex-1 min-w-0: a native <input> has an intrinsic ~20ch
                                width; without min-w-0 the flex child can't shrink and
                                spills into the GST column. */}
                            <Input
                              type="number"
                              value={item.discount_value}
                              onChange={(v) => updateItem(item.key, 'discount_value', v)}
                              placeholder="0"
                              className="flex-1 min-w-0"
                            />
                          </div>
                          {discountError && (
                            <p className="text-xs text-danger-600 mt-0.5">{discountError}</p>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Select
                            options={taxRateOptions(tenantCurrency, item.gst_rate)}
                            value={item.gst_rate}
                            onChange={(v) => updateItem(item.key, 'gst_rate', typeof v === 'string' ? v : '0')}
                          />
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                            {lineTotal > 0 ? formatDocAmount(lineTotal, currency) : '\u2014'}
                          </span>
                          {lineTotal > 0 && (line.line_discount > 0 || line.line_tax > 0) && (
                            <p className="text-[11px] leading-4 text-neutral-500 dark:text-neutral-400 tabular-nums">
                              {line.line_discount > 0 && <span className="block">{'\u2212'}{formatDocAmount(line.line_discount, currency)} disc</span>}
                              {line.line_tax > 0 && <span className="block">+{formatDocAmount(line.line_tax, currency)} tax</span>}
                            </p>
                          )}
                        </td>
                        <td className="py-2">
                          <button
                            type="button"
                            onClick={() => removeItemRow(item.key)}
                            disabled={items.length <= 1}
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Remove item"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Button variant="secondary" onClick={addItemRow} className="mt-2">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add Item
            </Button>

            {grandTotal > 0 && (
              <div className="flex justify-end mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <div className="text-right space-y-0.5">
                  {(totals.discount > 0 || totals.tax > 0) && (
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                      Subtotal {formatDocAmount(totals.subtotal, currency)}
                      {totals.discount > 0 && <> · Discount {'−'}{formatDocAmount(totals.discount, currency)}</>}
                      {totals.tax > 0 && <> · Tax {formatDocAmount(totals.tax, currency)}</>}
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-neutral-500 dark:text-neutral-400 mr-3">Grand Total</span>
                    <span className="text-xl font-bold text-neutral-900 dark:text-white tabular-nums">
                      {formatDocAmount(grandTotal, currency)}
                    </span>
                  </div>
                  {tenantGrandTotal != null && (
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                      ≈ {formatDocAmount(tenantGrandTotal, tenantCurrency)} at {exchangeRate}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review & Submit */}
        {stepIndex === 2 && (
          <div className="space-y-6">
            {/* Supplier Summary */}
            <div>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
                Supplier Details
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Supplier</dt>
                  <dd className="text-neutral-800 dark:text-neutral-200 font-medium">{supplierName}</dd>
                </div>
                <div>
                  <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Location</dt>
                  <dd className="text-neutral-800 dark:text-neutral-200">
                    {locations.find((l) => l.id === locationId)?.name ?? locationId}
                  </dd>
                </div>
                {supplierPhone && (
                  <div>
                    <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Phone</dt>
                    <dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">{supplierPhone}</dd>
                  </div>
                )}
                {supplierEmail && (
                  <div>
                    <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Email</dt>
                    <dd className="text-neutral-800 dark:text-neutral-200">{supplierEmail}</dd>
                  </div>
                )}
                {expectedDate && (
                  <div>
                    <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Expected Delivery</dt>
                    <dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">
                      {new Date(expectedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </dd>
                  </div>
                )}
                {isForeignCurrency && (
                  <div>
                    <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Currency</dt>
                    <dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">
                      {currency} · 1 {currency} = {exchangeRate} {tenantCurrency}
                    </dd>
                  </div>
                )}
                {notes && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Notes</dt>
                    <dd className="text-neutral-800 dark:text-neutral-200">{notes}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Items Summary */}
            <div>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
                Items ({validItems.length})
              </h3>
              {costToConfirm > 0 && (
                <p className="mb-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 px-3 py-2 text-xs text-warning-800 dark:text-warning-200">
                  {costToConfirm} line(s) have no cost yet. The PO is saved as a draft, and the owner confirms the costs
                  before approving.
                </p>
              )}
              <PlaceOfSupply supply={supply} />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 dark:border-neutral-700">
                      <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Product</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Qty</th>
                      <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Unit</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Unit Cost</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Discount</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Tax</th>
                      <th className="text-right py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validItems.map((item) => {
                      const line = lineTotals.get(item.key) ?? computePoLine(toPoLineInput(item, currency), tenantCurrency);
                      const input = toPoLineInput(item, currency);
                      return (
                        <tr key={item.key} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
                          <td className="py-2.5 pr-3 text-neutral-800 dark:text-neutral-200 font-medium">
                            {item.product_name}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                            {input.quantity}
                          </td>
                          <td className="py-2.5 pr-3 text-neutral-500 dark:text-neutral-400">
                            {item.unit}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                            {input.unit_cost > 0 ? (
                              formatDocAmount(input.unit_cost, currency)
                            ) : (
                              <span className="inline-flex rounded-full bg-warning-100 dark:bg-warning-900/40 px-2 py-0.5 text-[11px] font-semibold text-warning-700 dark:text-warning-300 whitespace-nowrap">
                                To be confirmed
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                            {formatDiscountCell(
                              line.line_discount,
                              item.discount_type,
                              item.discount_value,
                              currency,
                            )}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                            <GstTaxCell supply={supply} gstRate={input.gst_rate ?? 0} lineTax={line.line_tax} currency={currency} />
                          </td>
                          <td className="py-2.5 text-right font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                            {formatDocAmount(line.line_total, currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <dl className="w-72 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-neutral-500 dark:text-neutral-400">Subtotal</dt>
                    <dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">{formatDocAmount(totals.subtotal, currency)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-neutral-500 dark:text-neutral-400">Discount</dt>
                    <dd className="text-success-600 dark:text-success-400 tabular-nums">
                      {totals.discount > 0 ? `−${formatDocAmount(totals.discount, currency)}` : '—'}
                    </dd>
                  </div>
                  {taxLines.length > 0 ? (
                    taxLines.map((l) => (
                      <div key={`${l.kind}-${l.ratePct}`} className="flex justify-between">
                        <dt className="text-neutral-500 dark:text-neutral-400">{l.kind} {l.ratePct}%</dt>
                        <dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">{formatDocAmount(l.amount, currency)}</dd>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between">
                      <dt className="text-neutral-500 dark:text-neutral-400">Tax</dt>
                      <dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">
                        {totals.tax > 0 ? formatDocAmount(totals.tax, currency) : '—'}
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-neutral-200 dark:border-neutral-700">
                    <dt className="text-sm text-neutral-500 dark:text-neutral-400">Grand Total</dt>
                    <dd className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">
                      {formatDocAmount(grandTotal, currency)}
                    </dd>
                  </div>
                  {tenantGrandTotal != null && (
                    <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400">
                      <dt>In {tenantCurrency}</dt>
                      <dd className="tabular-nums">≈ {formatDocAmount(tenantGrandTotal, tenantCurrency)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <div>
            {stepIndex === 0 ? (
              <Button variant="secondary" onClick={() => navigate('/inventory/purchase-orders')}>
                <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
                Cancel
              </Button>
            ) : (
              <Button variant="secondary" onClick={handleBack}>
                <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
                Back
              </Button>
            )}
          </div>
          <div>
            {stepIndex < 2 ? (
              <Button variant="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending || validItems.length === 0}
              >
                {createMutation.isPending || updateMutation.isPending ? labels.submitting : labels.submit}
              </Button>
            )}
          </div>
        </div>
      </div>
      {leaveDialog}
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function PurchaseOrderCreatePage() {
  const { id: editId } = useParams<{ id: string }>();
  const { data: editingPo } = usePurchaseOrder(editId);
  const labels = poFormLabels(Boolean(editId), editingPo?.po_number);
  return (
    <PageContainer
      title={labels.title}
      breadcrumbs={[
        { label: 'Purchase' },
        { label: 'Purchase Orders', href: '/purchase-orders' },
        { label: labels.crumb },
      ]}
    >
      <ErrorBoundary>
        <PurchaseOrderCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
